/**
 * Read-only view of the UDP ingestion pipeline metrics in the admin /api/stats
 * snapshot (written by the udp process's StatsSnapshotter; see
 * application/jobs/stats_snapshotter.py). Pure projection: it decides nothing and
 * derives nothing about charging — it only says what the backend reported.
 *
 * One rule throughout: a number the backend sent (including 0) is a value; a
 * field it did not send is `null` ("not collected"). An older snapshot without the
 * newer sections therefore renders as "n/a", never as a fabricated zero.
 *
 * Labels: `label` is the metric's stable identity (React key, `data-metric`, tests);
 * a backend stage/source/outcome name is also shown as is. A fixed label has a
 * `labelKey` and is shown from the catalog; the panel translates at render.
 */

import { formatNumber, type MessageParams } from "@voltie/localization-web";
import { localization, type Translate } from "@/app/localization";

export type MetricValue = number | string | boolean | null;

/** Unit shown next to a value, as operator.ops.udp.unit renders it. */
export type MetricUnit = "us_per_packet" | "us_per_unit" | "ms" | "s" | "packets";

export interface Metric {
  label: string;
  labelKey?: string;
  value: MetricValue;
  unit?: MetricUnit;
  /** Backend text shown as is (e.g. the time of a peak). */
  hint?: string;
  /** A fixed hint from the catalog. */
  hintKey?: string;
  hintParams?: MessageParams;
}

export interface CircuitView {
  plane: "control" | "live";
  /** "closed" | "open" | "half_open"; null = not reported. */
  state: string | null;
  openCount: number | null;
  /** Live plane only: true when the backend reports no separate live breaker. */
  shared?: boolean;
}

/** One child stage of a breakdown. Every number is null when the stage has no
 * sample in this snapshot (it did not run, or the field is not reported). */
export interface StageRow {
  name: string;
  samples: number | null;
  cpuUsAvg: number | null;
  wallUsAvg: number | null;
  cpuUsMax: number | null;
  wallUsMax: number | null;
  cpuUsPerPacket: number | null;
  wallUsPerPacket: number | null;
}

/** The children of one parent stage (unit_update / command) on the timed packets. */
export interface StageBreakdown {
  parent: "unit_update" | "unit_lifecycle" | "command";
  /** Children on the common path, in hot-path order. */
  common: StageRow[];
  /** New-unit / restore-only children, kept apart from the common path. */
  rare: StageRow[];
  /** Parent time no child covers, per timed packet. */
  unattributedCpuUsPerPacket: number | null;
  unattributedWallUsPerPacket: number | null;
}

export interface HotPathView {
  /** null = the snapshot has no hot_path_stages section (older backend). */
  enabled: boolean | null;
  sampleEvery: number | null;
  packetsTimed: number | null;
  cpuUsPerPacketTotal: number | null;
  stages: Metric[];
  /** null = the snapshot predates the breakdown; false = sampling without it. */
  breakdownEnabled: boolean | null;
  /** Empty unless the snapshot carries a breakdown. */
  breakdowns: StageBreakdown[];
  /** Where the gate verdict of the timed packets came from (counts). */
  gateReadOutcomes: Metric[];
  lapCostUs: number | null;
  lapsPerTimedPacket: number | null;
}

/** The few values shown on the collapsed panel row. */
export interface UdpPipelineSummary {
  queueCurrent: number | null;
  dropped: number | null;
  processingErrors: number | null;
  pressureHigh: boolean | null;
}

export interface UdpPipelineView {
  summary: UdpPipelineSummary;
  queue: Metric[];
  redis: Metric[];
  circuits: CircuitView[];
  sampleProjectionMode: string | null;
  sampleProjection: Metric[];
  flusherStages: Metric[];
  hotPath: HotPathView;
  backgroundPressure: Metric[];
  sessionProcessor: Metric[];
  gateEval: Metric[];
  gateEvalOutcomes: Metric[];
}

type Obj = Record<string, unknown>;

function asObj(v: unknown): Obj | null {
  return v !== null && typeof v === "object" && !Array.isArray(v) ? (v as Obj) : null;
}

function get(root: unknown, path: string[]): unknown {
  let cur: unknown = root;
  for (const key of path) {
    const o = asObj(cur);
    if (!o || !(key in o)) return undefined;
    cur = o[key];
  }
  return cur;
}

/** A finite number at `path`, else null. 0 stays 0. */
export function num(root: unknown, ...path: string[]): number | null {
  const v = get(root, path);
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function str(root: unknown, ...path: string[]): string | null {
  const v = get(root, path);
  return typeof v === "string" ? v : null;
}

function bool(root: unknown, ...path: string[]): boolean | null {
  const v = get(root, path);
  return typeof v === "boolean" ? v : null;
}

const PACKET_STAGES = [
  "parse", "unit_update", "lifecycle", "session_projection_prep",
  "telemetry_projection", "session_projection", "redis_stage", "command", "log",
] as const;
// Already inside unit_update (application/hot_path_stages.NESTED_STAGES).
const NESTED_STAGES = new Set(["lifecycle", "session_projection_prep"]);
const FLUSH_STAGES = ["materialize", "build", "execute", "publish"] as const;
// application/hot_path_stages.BREAKDOWN parents, and GATE_READ_OUTCOMES order.
// unit_lifecycle is itself a child of unit_update, broken down one level deeper.
const BREAKDOWN_PARENTS = ["unit_update", "unit_lifecycle", "command"] as const;
const GATE_READ_OUTCOMES = [
  "memory_hit", "memory_miss", "redis_read", "redis_read_error", "breaker_open",
  "missing_gate", "valid_cached_verdict", "valid_redis_verdict", "fail_static",
  "error_default", "unclassified",
] as const;

function stageRow(stages: unknown, name: string): StageRow {
  const samples = num(stages, name, "samples");
  // No sample: the stage did not run in this process (yet) — n/a, never 0.
  const measured = (key: string) => (samples ? num(stages, name, key) : null);
  return {
    name,
    samples,
    cpuUsAvg: measured("cpu_us_avg"),
    wallUsAvg: measured("wall_us_avg"),
    cpuUsMax: measured("cpu_us_max"),
    wallUsMax: measured("wall_us_max"),
    cpuUsPerPacket: measured("cpu_us_per_packet"),
    wallUsPerPacket: measured("wall_us_per_packet"),
  };
}

function breakdowns(hp: unknown): StageBreakdown[] {
  const out: StageBreakdown[] = [];
  for (const parent of BREAKDOWN_PARENTS) {
    const b = asObj(get(hp, ["breakdown", parent]));
    const stages = asObj(b?.stages);
    if (!b || !stages) continue;
    const rare = new Set(Array.isArray(b.rare) ? b.rare.filter((x) => typeof x === "string") : []);
    const rows = Object.keys(stages).map((name) => stageRow(stages, name));
    out.push({
      parent,
      common: rows.filter((r) => !rare.has(r.name)),
      rare: rows.filter((r) => rare.has(r.name)),
      unattributedCpuUsPerPacket: num(b, "unattributed", "cpu_us_per_packet"),
      unattributedWallUsPerPacket: num(b, "unattributed", "wall_us_per_packet"),
    });
  }
  return out;
}

function circuit(stats: unknown, key: string, plane: "control" | "live"): CircuitView {
  const raw = get(stats, [key]);
  if (plane === "live" && raw === null) {
    // The backend sends null when unit:tel shares the control Redis.
    return { plane, state: null, openCount: null, shared: true };
  }
  return { plane, state: str(raw, "state"), openCount: num(raw, "open_count") };
}

export function udpPipelineView(stats: unknown): UdpPipelineView {
  const rf = get(stats, ["redis_flush"]);
  const sp = get(rf, ["sample_projection"]);
  const hp = get(stats, ["hot_path_stages"]);
  const sproc = get(stats, ["session_processor"]);
  const pressure = get(stats, ["background", "pressure"]);
  const window = num(stats, "queue_peak_window_seconds");

  const hpEnabled = asObj(hp) ? bool(hp, "enabled") : null;
  const hotPath: HotPathView = {
    enabled: hpEnabled,
    sampleEvery: num(hp, "sample_every"),
    packetsTimed: num(hp, "packets_timed"),
    cpuUsPerPacketTotal: num(hp, "cpu_us_per_packet_total"),
    stages: hpEnabled
      ? PACKET_STAGES.map((name) => ({
          label: name,
          value: num(hp, "stages", name, "cpu_us_per_packet"),
          unit: "us_per_packet",
          hintKey: NESTED_STAGES.has(name) ? "operator.ops.udp.hint_inside_unit_update" : undefined,
        }))
      : [],
    breakdownEnabled: hpEnabled ? bool(hp, "breakdown_enabled") : null,
    breakdowns: hpEnabled ? breakdowns(hp) : [],
    gateReadOutcomes: hpEnabled && asObj(get(hp, ["gate_read_outcomes"]))
      ? GATE_READ_OUTCOMES.map((name) => ({
          label: name, value: num(hp, "gate_read_outcomes", name),
        }))
      : [],
    lapCostUs: hpEnabled ? num(hp, "lap_cost_us") : null,
    lapsPerTimedPacket: hpEnabled ? num(hp, "laps_per_timed_packet") : null,
  };

  const outcomes = asObj(get(stats, ["gate_eval_outcomes"]));
  const sources = asObj(get(pressure, ["sources"]));
  const reason = get(pressure, ["reason"]);

  return {
    summary: {
      queueCurrent: num(stats, "queue_current"),
      dropped: num(stats, "messages_dropped"),
      processingErrors: num(stats, "processing_errors"),
      pressureHigh: bool(pressure, "high"),
    },
    queue: [
      { label: "Current", labelKey: "operator.ops.udp.queue_current", value: num(stats, "queue_current") },
      { label: "Peak (recent window)", labelKey: "operator.ops.udp.queue_peak_recent", value: num(stats, "queue_peak_recent"),
        hintKey: window !== null ? "operator.ops.udp.hint_recent_window" : undefined,
        hintParams: window !== null ? { seconds: window } : undefined },
      { label: "Peak (process lifetime)", labelKey: "operator.ops.udp.queue_peak_lifetime", value: num(stats, "queue_peak"),
        hint: str(stats, "queue_peak_at") ?? undefined },
      { label: "Capacity", labelKey: "operator.ops.udp.queue_capacity", value: num(stats, "queue_max") },
      { label: "Dropped", labelKey: "operator.ops.udp.dropped", value: num(stats, "messages_dropped") },
      { label: "Processing errors", labelKey: "operator.ops.udp.processing_errors", value: num(stats, "processing_errors") },
    ],
    redis: [
      { label: "Pending now", labelKey: "operator.ops.udp.redis_pending_now", value: num(rf, "pending_now") },
      { label: "Critical pending", labelKey: "operator.ops.udp.redis_critical_pending", value: num(rf, "critical_pending_now") },
      { label: "Telemetry pending", labelKey: "operator.ops.udp.redis_telemetry_pending", value: num(rf, "telemetry_pending_now") },
      { label: "Deferred (last cycle)", labelKey: "operator.ops.udp.redis_deferred", value: num(rf, "deferred_pending"),
        hintKey: "operator.ops.udp.hint_deferred" },
      { label: "Peak deferred", labelKey: "operator.ops.udp.redis_peak_deferred", value: num(rf, "peak_deferred_pending"),
        hintKey: "operator.ops.udp.hint_log_window" },
      { label: "Peak pending", labelKey: "operator.ops.udp.redis_peak_pending", value: num(rf, "peak_pending"),
        hintKey: "operator.ops.udp.hint_log_window" },
      { label: "Flush errors", labelKey: "operator.ops.udp.redis_flush_errors", value: num(rf, "errors"),
        hintKey: "operator.ops.udp.hint_log_window" },
      { label: "Oldest critical wait", labelKey: "operator.ops.udp.redis_oldest_critical_wait", value: num(rf, "critical_oldest_wait_ms"), unit: "ms" },
      { label: "Oldest telemetry wait", labelKey: "operator.ops.udp.redis_oldest_telemetry_wait", value: num(rf, "telemetry_oldest_wait_ms"), unit: "ms" },
    ],
    circuits: [circuit(stats, "redis_circuit", "control"),
               circuit(stats, "redis_live_circuit", "live")],
    sampleProjectionMode: str(sp, "mode"),
    sampleProjection: [
      { label: "Staged", labelKey: "operator.ops.udp.projection_staged", value: num(sp, "staged") },
      { label: "Telemetry superseded", labelKey: "operator.ops.udp.projection_tel_superseded", value: num(sp, "tel_superseded") },
      { label: "Session superseded", labelKey: "operator.ops.udp.projection_session_superseded", value: num(sp, "session_superseded") },
      { label: "Stale (refused)", labelKey: "operator.ops.udp.projection_stale", value: num(sp, "stale") },
      { label: "Telemetry materialized", labelKey: "operator.ops.udp.projection_tel_materialized", value: num(sp, "tel_materialized") },
      { label: "Session materialized", labelKey: "operator.ops.udp.projection_session_materialized", value: num(sp, "session_materialized") },
    ],
    flusherStages: [
      { label: "Units flushed", labelKey: "operator.ops.udp.units_flushed", value: num(rf, "stages", "units") },
      ...FLUSH_STAGES.map((name): Metric => ({
        label: name,
        value: num(rf, "stages", name, "cpu_us_per_unit"),
        unit: "us_per_unit",
      })),
    ],
    hotPath,
    backgroundPressure: [
      { label: "High", labelKey: "operator.ops.udp.pressure_high", value: bool(pressure, "high") },
      { label: "Reason", labelKey: "operator.ops.udp.pressure_reason", value: Array.isArray(reason) ? (reason.length ? reason.join(", ") : "—") : null },
      { label: "High for", labelKey: "operator.ops.udp.pressure_high_for", value: num(pressure, "high_for_s"), unit: "s" },
      { label: "Transitions", labelKey: "operator.ops.udp.pressure_transitions", value: num(pressure, "transitions") },
      ...Object.keys(sources ?? {}).sort().map((name): Metric => {
        const threshold = num(sources, name, "high");
        return {
          label: name,
          value: num(sources, name, "value"),
          hintKey: threshold !== null ? "operator.ops.udp.hint_high_threshold" : undefined,
          hintParams: threshold !== null ? { threshold: formatMetric(threshold) } : undefined,
        };
      }),
    ],
    sessionProcessor: sessionProcessor(sproc),
    gateEval: [
      { label: "Queue depth", labelKey: "operator.ops.udp.queue_depth", value: num(stats, "gate_eval_queue_depth") },
      { label: "Dropped", labelKey: "operator.ops.udp.dropped", value: num(stats, "gate_eval_dropped") },
    ],
    gateEvalOutcomes: Object.keys(outcomes ?? {}).sort().map((name) => ({
      label: name, value: num(outcomes, name),
    })),
  };
}

function sessionProcessor(sproc: unknown): Metric[] {
  const events = num(sproc, "events_processed");
  // Latencies are averages over the events of the last snapshot interval: with no
  // event in it the backend's 0 is "no sample", not a measured 0 ms.
  const latency = (key: string): number | null => (events === 0 ? null : num(sproc, key));
  const noEvents = events === 0 ? "operator.ops.udp.hint_no_events" : undefined;
  return [
    { label: "Events (last interval)", labelKey: "operator.ops.udp.session_events", value: events },
    { label: "Failed (last interval)", labelKey: "operator.ops.udp.session_failed", value: num(sproc, "events_failed") },
    { label: "Queue depth", labelKey: "operator.ops.udp.queue_depth", value: num(sproc, "queue_depth") },
    { label: "Avg latency", labelKey: "operator.ops.udp.session_avg_latency", value: latency("avg_ms"), unit: "ms", hintKey: noEvents },
    { label: "Peak latency", labelKey: "operator.ops.udp.session_peak_latency", value: latency("peak_ms"), unit: "ms", hintKey: noEvents },
    { label: "Avg save", labelKey: "operator.ops.udp.session_avg_save", value: latency("avg_save_ms"), unit: "ms", hintKey: noEvents },
  ];
}

/** Display text of a metric value: null → "n/a"; numbers keep a real 0. Callers rendering
 *  it pass their `t`; without one it uses the runtime's active catalog at call time. */
export function formatMetric(value: MetricValue, t: Translate = localization.t): string {
  if (value === null) return t("operator.ops.udp.na");
  if (typeof value === "boolean") return t("operator.ops.yes_no", { value: value ? "yes" : "no" });
  if (typeof value === "number") {
    return formatNumber(value, Number.isInteger(value) ? 0 : 2);
  }
  return value;
}

/** Shown label of a metric: its catalog label, or the backend name as is. */
export function metricLabel(t: Translate, m: Metric): string {
  return m.labelKey ? t(m.labelKey) : m.label;
}

/** Shown hint of a metric, if any. */
export function metricHint(t: Translate, m: Metric): string | undefined {
  return m.hintKey ? t(m.hintKey, m.hintParams) : m.hint;
}
